import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const errText = (detail, fallback) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
};

export const PasswordLoginForm = ({ path = "/dashboard" }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/password/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errText(data.detail, t("pw_login_fail")));
      navigate(path, { state: { user: data }, replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const forgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/password/forgot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) throw new Error(t("pw_forgot_fail"));
      setSent(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const input = "field-input field-input-dark w-full";

  if (mode === "forgot") {
    return (
      <form onSubmit={forgot} className="mt-6 text-left space-y-3" data-testid="pw-forgot-form">
        <p className="text-xs text-slate-400">{t("pw_forgot_sub")}</p>
        {sent ? (
          <p className="rounded-xl bg-[#25D366]/10 border border-[#25D366]/30 px-4 py-3 text-sm text-[#25D366]" data-testid="pw-forgot-sent">
            {t("pw_forgot_sent")}
          </p>
        ) : (
          <>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("pw_email_ph")} className={input} data-testid="pw-forgot-email-input" />
            <button type="submit" disabled={busy} className="w-full rounded-full border border-white/20 text-white font-display font-bold px-6 py-3 text-sm hover:border-[#F26522] transition-colors duration-200 disabled:opacity-60" data-testid="pw-forgot-submit-btn">
              {busy ? t("pw_sending") : t("pw_forgot_btn")}
            </button>
          </>
        )}
        <button type="button" onClick={() => { setMode("login"); setSent(false); }} className="w-full py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#F26522] transition-colors duration-200" data-testid="pw-back-to-login-btn">
          {t("pw_back_login")}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={login} className="mt-6 text-left space-y-3" data-testid="pw-login-form">
      <div className="flex items-center gap-3 text-slate-500">
        <span className="h-px flex-1 bg-white/10" />
        <span className="font-mono text-[10px] uppercase tracking-[0.25em]">{t("pw_or")}</span>
        <span className="h-px flex-1 bg-white/10" />
      </div>
      <div className="relative">
        <Mail className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <input type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("pw_email_ph")} className={`${input} !pl-11`} data-testid="pw-email-input" />
      </div>
      <div className="relative">
        <KeyRound className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <input type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("pw_password_ph")} className={`${input} !pl-11`} data-testid="pw-password-input" />
      </div>
      <button type="submit" disabled={busy} className="w-full rounded-full border border-white/20 text-white font-display font-bold px-6 py-3 text-sm hover:border-[#F26522] hover:bg-white/5 transition-colors duration-200 disabled:opacity-60" data-testid="pw-login-submit-btn">
        {busy ? t("pw_signing_in") : t("pw_login_btn")}
      </button>
      <button type="button" onClick={() => setMode("forgot")} className="w-full py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#F26522] transition-colors duration-200" data-testid="pw-forgot-link">
        {t("pw_forgot_link")}
      </button>
    </form>
  );
};
